import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Zap, Trophy, Timer, CheckCircle, XCircle } from 'lucide-react';

const LightningQuiz = ({ onGameComplete, gameConfig }) => {
  const [gameState, setGameState] = useState('waiting');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [timeLeft, setTimeLeft] = useState(gameConfig.duration);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  
  const questions = [
    {
      question: "What is the capital of India?",
      options: ["Mumbai", "New Delhi", "Kolkata", "Chennai"],
      correct: 1
    },
    {
      question: "Which planet is known as the Red Planet?",
      options: ["Venus", "Jupiter", "Mars", "Saturn"],
      correct: 2
    },
    {
      question: "Who invented the telephone?",
      options: ["Alexander Graham Bell", "Thomas Edison", "Nikola Tesla", "Albert Einstein"],
      correct: 0
    },
    {
      question: "What is the largest mammal in the world?",
      options: ["Elephant", "Blue Whale", "Giraffe", "Rhino"],
      correct: 1
    },
    {
      question: "In which year did India gain independence?",
      options: ["1945", "1946", "1947", "1948"],
      correct: 2
    },
    {
      question: "What is the chemical symbol for gold?",
      options: ["Go", "Gd", "Au", "Ag"],
      correct: 2
    },
    {
      question: "Which is the longest river in the world?",
      options: ["Amazon", "Nile", "Ganges", "Mississippi"],
      correct: 1
    },
    {
      question: "How many continents are there?",
      options: ["5", "6", "7", "8"],
      correct: 2
    },
    {
      question: "What is the fastest land animal?",
      options: ["Lion", "Tiger", "Cheetah", "Leopard"],
      correct: 2
    },
    {
      question: "Which gas makes up most of Earth's atmosphere?",
      options: ["Oxygen", "Carbon Dioxide", "Nitrogen", "Hydrogen"],
      correct: 2
    },
    {
      question: "Who wrote 'Romeo and Juliet'?",
      options: ["Charles Dickens", "William Shakespeare", "Jane Austen", "Mark Twain"],
      correct: 1
    },
    {
      question: "What is the smallest prime number?",
      options: ["0", "1", "2", "3"],
      correct: 2
    },
    {
      question: "Which country has the most time zones?",
      options: ["USA", "Russia", "China", "Canada"],
      correct: 1
    },
    {
      question: "What is the hardest natural substance?",
      options: ["Gold", "Iron", "Diamond", "Platinum"],
      correct: 2
    },
    {
      question: "How many hearts does an octopus have?",
      options: ["1", "2", "3", "4"],
      correct: 2
    }
  ];
  
  const [gameQuestions, setGameQuestions] = useState([]);
  
  // Shuffle and select random questions for this game
  const initializeQuestions = () => {
    const shuffled = [...questions].sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, Math.min(10, questions.length));
    setGameQuestions(selected);
  };
  
  // Start game
  const startGame = () => {
    initializeQuestions();
    setGameState('playing');
    setScore(0);
    setCorrectAnswers(0);
    setTimeLeft(gameConfig.duration);
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setQuestionStartTime(Date.now());
  };
  
  // Game timer
  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(time => time - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (gameState === 'playing' && timeLeft === 0) {
      endGame();
    }
  }, [gameState, timeLeft]);
  
  // Handle answer selection
  const handleAnswerClick = (answerIndex) => {
    if (selectedAnswer !== null || showResult) return;
    
    setSelectedAnswer(answerIndex);
    setShowResult(true);
    
    const currentQ = gameQuestions[currentQuestion];
    const isCorrect = answerIndex === currentQ.correct;
    const responseTime = Date.now() - questionStartTime;
    
    if (isCorrect) {
      // Calculate score based on speed (bonus for faster answers)
      const speedBonus = Math.max(0, 5000 - responseTime) / 50; // Max 100 bonus points
      const questionScore = Math.floor(100 + speedBonus);
      setScore(prev => prev + questionScore);
      setCorrectAnswers(prev => prev + 1);
    }
    
    // Move to next question after 1.5 seconds
    setTimeout(() => {
      if (currentQuestion + 1 >= gameQuestions.length || timeLeft <= 1) {
        endGame();
      } else {
        setCurrentQuestion(prev => prev + 1);
        setSelectedAnswer(null);
        setShowResult(false);
        setQuestionStartTime(Date.now());
      }
    }, 1500);
  };
  
  // End game
  const endGame = () => {
    setGameState('finished');
    
    const accuracy = gameQuestions.length > 0 ? (correctAnswers / Math.min(currentQuestion + 1, gameQuestions.length) * 100) : 0;
    const gameData = {
      questionsAnswered: currentQuestion + (selectedAnswer !== null ? 1 : 0),
      correctAnswers: correctAnswers,
      accuracy: accuracy.toFixed(1),
      finalScore: score,
      timeRemaining: timeLeft
    };
    
    setTimeout(() => {
      onGameComplete(score, gameData);
    }, 3000);
  };
  
  if (gameState === 'waiting') {
    return (
      <div className="text-center">
        <Card className="gaming-card max-w-lg mx-auto p-8">
          <CardHeader className="pb-6">
            <Zap className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <CardTitle className="text-2xl text-white">Lightning Quiz</CardTitle>
            <p className="text-gray-400">
              Answer as many questions correctly as possible! Speed and accuracy both matter.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Duration:</span>
                <span className="text-white">{gameConfig.duration} seconds</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Questions:</span>
                <span className="text-white">Random selection of 10</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Scoring:</span>
                <span className="text-white">Speed + Accuracy bonus</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Entry Fee:</span>
                <span className="text-yellow-400 font-semibold">₹10</span>
              </div>
            </div>
            <Button onClick={startGame} className="w-full btn-primary py-3 text-lg">
              Start Quiz
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (gameState === 'finished') {
    const accuracy = gameQuestions.length > 0 ? (correctAnswers / Math.min(currentQuestion + 1, gameQuestions.length) * 100) : 0;
    
    return (
      <div className="text-center">
        <Card className="gaming-card max-w-lg mx-auto p-8">
          <CardHeader className="pb-6">
            <Trophy className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <CardTitle className="text-2xl text-white">Quiz Complete!</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 mb-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">{score}</div>
                <div className="text-gray-400">Final Score</div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-green-400">{correctAnswers}/{Math.min(currentQuestion + 1, gameQuestions.length)}</div>
                  <div className="text-gray-400 text-sm">Correct Answers</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-400">{accuracy.toFixed(1)}%</div>
                  <div className="text-gray-400 text-sm">Accuracy</div>
                </div>
              </div>
            </div>
            
            <div className="text-gray-400 text-sm">
              {accuracy >= 80 ? 'Excellent knowledge!' : 
               accuracy >= 60 ? 'Good performance!' : 'Keep learning and try again!'}<br/>
              Returning to games lobby in 3 seconds...
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const currentQ = gameQuestions[currentQuestion];
  if (!currentQ) return null;
  
  return (
    <div className="max-w-2xl mx-auto">
      {/* Game HUD */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex space-x-4">
          <Badge className="bg-yellow-400/20 text-yellow-400 border-yellow-400/30 px-4 py-2">
            Score: {score}
          </Badge>
          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 px-4 py-2">
            {currentQuestion + 1}/{gameQuestions.length}
          </Badge>
        </div>
        <Badge className="bg-red-500/20 text-red-400 border-red-500/30 px-4 py-2 flex items-center space-x-2">
          <Timer className="w-4 h-4" />
          <span>{timeLeft}s</span>
        </Badge>
      </div>
      
      {/* Question Card */}
      <Card className="gaming-card p-8 mb-6">
        <CardHeader className="pb-6">
          <CardTitle className="text-xl text-white text-center leading-relaxed">
            {currentQ.question}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3">
            {currentQ.options.map((option, index) => {
              let buttonClass = "w-full p-4 text-left rounded-lg transition-all duration-200 border-2 ";
              
              if (!showResult) {
                buttonClass += "border-gray-600 bg-gray-800 hover:bg-gray-700 hover:border-yellow-400 text-white";
              } else {
                if (index === currentQ.correct) {
                  buttonClass += "border-green-500 bg-green-500/20 text-green-400";
                } else if (index === selectedAnswer && index !== currentQ.correct) {
                  buttonClass += "border-red-500 bg-red-500/20 text-red-400";
                } else {
                  buttonClass += "border-gray-600 bg-gray-800 text-gray-400";
                }
              }
              
              return (
                <button
                  key={index}
                  onClick={() => handleAnswerClick(index)}
                  disabled={showResult}
                  className={buttonClass}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-sm font-bold">
                      {String.fromCharCode(65 + index)}
                    </div>
                    <span className="flex-1">{option}</span>
                    {showResult && index === currentQ.correct && (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    )}
                    {showResult && index === selectedAnswer && index !== currentQ.correct && (
                      <XCircle className="w-5 h-5 text-red-400" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
      
      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-400 mb-2">
          <span>Progress</span>
          <span>{correctAnswers} correct</span>
        </div>
        <div className="w-full bg-gray-800 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-yellow-400 to-orange-400 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentQuestion + 1) / gameQuestions.length) * 100}%` }}
          />
        </div>
      </div>
      
      {/* Game Instructions */}
      <div className="text-center text-gray-400 text-sm">
        {!showResult ? 'Choose the correct answer quickly for bonus points!' :
         selectedAnswer === currentQ.correct ? '✓ Correct! Moving to next question...' :
         '✗ Wrong answer. Moving to next question...'}
      </div>
    </div>
  );
};

export default LightningQuiz;